import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTenants } from '../../hooks/tenants/useTenants';
import { useParkingQueueMutations } from '../../hooks/parkingQueue';
import { useLeaseQueueMatch } from '../../hooks/parkingQueue/useLeaseQueueMatch';
import { useQueueDeviation } from '../../hooks/parkingQueue/useQueueDeviation';
import { Lease } from '../../types/leases';
import { ParkingQueueEntry, QUEUE_CLOSE_REASON_LABELS } from '../../types/parkingQueue';
import DeviationNotice from './DeviationNotice';

interface Props {
    /** The parking lease just written; null renders nothing. */
    lease: Lease | null;
    /** Called once the question is answered, or right away when no entry matches. */
    onDone: () => void;
}

const NONE = '';

const entrySummary = (entry: ParkingQueueEntry): string => {
    const areas = Object.keys(entry.wantedAreas).length;
    return `Ködatum ${entry.queueDate} · ${areas === 1 ? '1 önskat område' : `${areas} önskade områden`}${entry.comment ? ` · ${entry.comment}` : ''}`;
};

/**
 * After a parking lease written outside the queue (the spot page, Hyresavtal):
 * when the tenant has an active köplats wanting the spot's area, offers to
 * close it as "Fick hyresavtal". One lease closes at most one entry, so
 * several matches are a choice with "Stäng ingen". The deviation rule is the
 * queue flow's: someone ahead requires a reason. Declining writes nothing.
 */
const LeaseQueueCloseModal: React.FC<Props> = ({ lease, onDone }) => {
    const { getTenantName } = useTenants();
    const { entries } = useLeaseQueueMatch(lease);
    const mutations = useParkingQueueMutations(async () => undefined);
    const [selected, setSelected] = useState<string>(NONE);
    const [reason, setReason] = useState('');
    const [tried, setTried] = useState(false);
    const target = { buildingKey: lease?.buildingKey, areaKey: lease?.object.areaKey, spotKey: lease?.object.key };
    const ahead = useQueueDeviation(selected || null, target, lease?.key);

    useEffect(() => {
        if (entries && entries.length === 0) onDone();
        if (entries && entries.length > 0) setSelected(entries[0].key);
        setReason('');
        setTried(false);
        // onDone is the caller's close handler; only a new result matters here.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries]);

    if (!lease || !entries || entries.length === 0) return null;

    const reasonMissing = !!ahead && ahead.length > 0 && !reason.trim();
    const confirm = async () => {
        if (!selected) { onDone(); return; }
        setTried(true);
        if (reasonMissing || !target.buildingKey || !target.areaKey || !target.spotKey) return;
        try {
            await mutations.linkLease(selected, {
                buildingKey: target.buildingKey, areaKey: target.areaKey, spotKey: target.spotKey,
                leaseKey: lease.key, reason: reason.trim() || undefined,
            });
            onDone();
        } catch {
            // shown from mutations.error
        }
    };

    return (
        <Modal show onHide={onDone}>
            <Modal.Header closeButton><Modal.Title>Stäng köplatsen?</Modal.Title></Modal.Header>
            <Modal.Body>
                {mutations.error && <Alert variant="danger">{mutations.error}</Alert>}
                <p>
                    {getTenantName(lease.tenantKey)} står i parkeringskön för området där {lease.object.label ?? 'platsen'} finns.
                    {' '}Stäng köplatsen som ”{QUEUE_CLOSE_REASON_LABELS.accepted}”?
                </p>
                {entries.length === 1 ? (
                    <p className="text-muted small">{entrySummary(entries[0])} · <Link to={`/parking-queue?entry=${entries[0].key}`}>Visa i kön</Link></p>
                ) : (
                    <Form.Group className="mb-3">
                        {entries.map(entry => (
                            <Form.Check key={entry.key} type="radio" id={`lease-queue-${entry.key}`} name="lease-queue-entry"
                                checked={selected === entry.key} onChange={() => { setSelected(entry.key); setReason(''); setTried(false); }}
                                label={entrySummary(entry)} />
                        ))}
                        <Form.Check type="radio" id="lease-queue-none" name="lease-queue-entry" label="Stäng ingen"
                            checked={selected === NONE} onChange={() => setSelected(NONE)} />
                    </Form.Group>
                )}
                {selected && (
                    <DeviationNotice ahead={ahead} reason={reason} onReasonChange={setReason}
                        error={tried && reasonMissing ? 'Ange anledning' : undefined} idPrefix="lease-queue" />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onDone} disabled={mutations.submitting}>Nej</Button>
                <Button variant="primary" onClick={confirm} disabled={mutations.submitting || (!!selected && ahead === null)}>
                    {mutations.submitting ? 'Sparar…' : selected ? 'Stäng köplatsen' : 'Klar'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default LeaseQueueCloseModal;
